/* eslint-disable no-restricted-syntax */
/**
 * GENERIC APP ENUMS
 */

// App IDs
export const FISH_APP_IDS = Object.freeze({
  IOS: '6466307868',
  ANDROID: 'onxmaps.fish',
})

export const MAPBOX_TOKEN =
  'pk.eyJ1Ijoib254bWFwcyIsImEiOiJj' +
  'azczb2tpazUwZmVxM2huMWpxNXhscmN' +
  'pIn0.rm-IQ9Kc-XKxS5m1YBR_4A'

// These should generally only be used as a fallback urls since they will not have
// any attribution analytics that come with tools like Kochava or Branch
export const APP_STORE_LINKS = Object.freeze({
  IOS_FISH: 'https://apps.apple.com/us/app/onx-fish-minnesota/id6466307868',
  ANDROID_FISH: 'https://play.google.com/store/apps/details?id=onxmaps.fish',
})

export const MONTHS = Object.freeze([
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
])

export const ANIMATION_TYPES = Object.freeze({
  FADE: 'fade',
  QUICK_FADE: 'quick_fade',
  FADE_TRANSLATE: 'fade_translate',
  IMAGE_FADE: 'image_fade',
  FADE_TRANSLATE_LEFT: 'fade_translate_left',
  FADE_TRANSLATE_RIGHT: 'fade_translate_right',
  TRANSLATE_LEFT: 'translate_left',
  TRANSLATE_RIGHT: 'translate_right',
})

export const STAT_UNITS = {
  MAX: 'max',
  HRS: 'hrs',
  MS: 'ms',
  AC: 'acres',
  MI2: 'mi\u00B2',
  KM2: 'km\u00B2',
  M2: 'm\u00B2',
  MPH: 'mph',
  KPH: 'kph',
}

export const STAT_TYPES = Object.freeze({
  DEFAULT: 'default',
  DISTANCE: 'distance',
  DURATION: 'duration',
  TIME: 'time',
  ICON: 'icon',
  CUSTOM: 'custom',
})

/*
 * Horizontal angle a given aspect faces
 * Horizontal angle a camera should position at to face that aspect
 */
export const ASPECT_BEARING = Object.freeze({
  N: {
    facing: 0,
    camera: 180,
  },
  NE: {
    facing: 35,
    camera: -135,
  },
  E: {
    facing: 90,
    camera: -90,
  },
  SE: {
    facing: 135,
    camera: -35,
  },
  S: {
    facing: 180,
    camera: 0,
  },
  SW: {
    facing: -135,
    camera: 35,
  },
  W: {
    facing: -90,
    camera: 90,
  },
  NW: {
    facing: -35,
    camera: 135,
  },
})

/**
 * BACKCOUNTRY ENUMS
 */

// Trail types
export const BACKCOUNTRY_TRAIL_TYPES = Object.freeze({
  ADVENTURE: 'adventures',
  TRAIL: 'trails',
})

/**
 * Trail difficulties
 * {
 *   id: the unique id specific to webmap for that difficulty
 *   keys: the strings that may identify the difficulty from the backend
 *   label: the label to be displayed in the UI
 *   icon: the icon that corresponds to that difficulty
 * }
 */
export const DIFFICULTIES = Object.freeze([
  {
    id: 'unknown',
    keys: ['Unknown'],
    label: 'Unknown',
    icon: 'question-mark-dashed-circle',
  },

  {
    id: 'easy',
    keys: ['Easy', 'EASY'],
    label: 'Easy',
    icon: 'green-circle',
  },
  {
    id: 'easy-intermediate',
    keys: ['Easy/Intermediate', 'EASY_INTERMEDIATE'],
    label: 'Easy/Int.',
    icon: 'green-circle-blue-square',
  },
  {
    id: 'moderate',
    keys: ['Moderate', 'MODERATE'],
    label: 'Moderate',
    icon: 'blue-square',
  },
  {
    id: 'intermediate',
    keys: ['Intermediate', 'INTERMEDIATE'],
    label: 'Intermediate',
    icon: 'blue-square',
  },
  {
    id: 'intermediate-difficult',
    keys: ['Intermediate/Difficult', 'INTERMEDIATE_DIFFICULT'],
    label: 'Int./Difficult',
    icon: 'black-diamond-blue-square',
  },
  {
    id: 'difficult',
    keys: ['Difficult', 'DIFFICULT'],
    label: 'Difficult',
    icon: 'black-diamond',
  },
  {
    id: 'strenuous',
    keys: ['Strenuous', 'STRENUOUS'],
    label: 'Strenuous',
    icon: 'black-diamond',
  },
  {
    id: 'very-strenuous',
    keys: ['Very Strenuous', 'VERY_STRENUOUS'],
    label: 'V. Strenuous',
    icon: 'overlapping-double-black-diamond',
  },
  {
    id: 'extremely-difficult',
    keys: ['Extremely Difficult', 'EXTREMELY_DIFFICULT'],
    label: 'Extreme',
    icon: 'overlapping-double-red-diamond',
  },
])

export const AVY_INFO = Object.freeze({
  low: {
    iconPath: 'backcountry/avy-low',
    miniIconPath: 'backcountry/avy-low_mini',
    label: 'Low',
  },
  moderate: {
    iconPath: 'backcountry/avy-moderate',
    miniIconPath: 'backcountry/avy-moderate_mini',
    label: 'Moderate',
  },
  considerable: {
    iconPath: 'backcountry/avy-considerable',
    miniIconPath: 'backcountry/avy-considerable_mini',
    label: 'Considerable',
  },
  high: {
    iconPath: 'backcountry/avy-high',
    miniIconPath: 'backcountry/avy-high_mini',
    label: 'High',
  },
  extreme: {
    iconPath: 'backcountry/avy-extreme',
    miniIconPath: 'backcountry/avy-extreme_mini',
    label: 'Extreme',
  },
  'no rating': {
    iconPath: 'backcountry/avy-no-rating',
    miniIconPath: 'backcountry/avy-no-rating_mini',
    label: 'No Rating',
  },
})

export const OBSERVATIONS_TYPE_MAP = Object.freeze({
  PROFESSIONAL: 'Professional observation',
  PUBLIC: 'Public observation',
  AVY: 'Avalanche',
  CRACKING: 'Cracking',
})

export const OBSERVATIONS_CONSTS = Object.freeze({
  PRO: 'PROFESSIONAL',
  PUBLIC: 'PUBLIC',
  AVY_TRIGGERED: 'AVALANCHE_TRIGGERED',
  AVY: 'AVALANCHE',
  CRACKING: 'CRACKING',
})

// Source constants
export const BASE = 'base'
export const STATIC_MARKUPS_SOURCE = 'static-markups'
export const WAYPOINTS_SOURCE = 'waypoints'
export const WAYPOINTS_LABEL_SOURCE = 'waypoint-labels'
export const HIGHLIGHTED_WAYPOINTS_SOURCE = 'highlighted-waypoints'
export const LINES_SOURCE = 'lines'
export const TRACKS_SOURCE = 'tracks'
export const SHAPES_SOURCE = 'shapes'
export const ROUTES_SOURCE = 'routes'
export const QUERY_MARKER_SOURCE = 'query-markers'
export const OFFLINE_MAPS_SOURCE = 'offline-maps'
export const OFFLINE_OVERLAY_SOURCE = 'offline-overlay'
export const RANGE_RADIUS_SOURCE = 'range-radius'
export const HIGHLIGHT_SOURCE = 'highlight'
export const ELEVATION_POINT_SOURCE = 'elevation-point'
export const MARKUP_EDIT_SOURCE = 'map-draw-source'
export const TERRARIUM_DEM_SOURCE = 'elevation-dem'
export const TRAIL_MARKER_SOURCE = 'trail-markers'
export const SNOW_OBSERVATION_SOURCE = 'snowpack-observation:location'
export const AVALANCHE_INCIDENTS_SOURCE = 'avalanche_incidents'
export const MOUNTAIN_PROJECT_SOURCE = 'mountain-project'
export const SPOT_SOURCE = 'spots'
export const SEARCH_PIN_SOURCE = 'search-results'
export const FAVORITED_WATERBODIES_SOURCE = 'water-special'
export const POINT_OF_INTEREST_SOURCE = 'rec_point'

// An array of all of the custom sources that we have defined
export const ALL_CUSTOM_SOURCES = [
  STATIC_MARKUPS_SOURCE,
  WAYPOINTS_SOURCE,
  WAYPOINTS_LABEL_SOURCE,
  HIGHLIGHTED_WAYPOINTS_SOURCE,
  LINES_SOURCE,
  TRACKS_SOURCE,
  SHAPES_SOURCE,
  ROUTES_SOURCE,
  OFFLINE_MAPS_SOURCE,
  OFFLINE_OVERLAY_SOURCE,
  RANGE_RADIUS_SOURCE,
  HIGHLIGHT_SOURCE,
  ELEVATION_POINT_SOURCE,
  MARKUP_EDIT_SOURCE,
  SNOW_OBSERVATION_SOURCE,
  SPOT_SOURCE,
  SEARCH_PIN_SOURCE,
]

// Some custom sources rely on feature ids being defined at the top-level (instead of in properties).
// See: https://docs.mapbox.com/mapbox-gl-js/style-spec/sources/#vector-promoteId
export const PROMOTE_IDS = {
  [STATIC_MARKUPS_SOURCE]: 'id',
  [WAYPOINTS_SOURCE]: 'id',
  [HIGHLIGHTED_WAYPOINTS_SOURCE]: 'id',
  [LINES_SOURCE]: 'id',
  [TRACKS_SOURCE]: 'id',
  [SHAPES_SOURCE]: 'id',
  [ROUTES_SOURCE]: 'id',
  [MARKUP_EDIT_SOURCE]: 'id',
  [OFFLINE_MAPS_SOURCE]: 'id',
  [SNOW_OBSERVATION_SOURCE]: 'id',
  [SPOT_SOURCE]: 'id',
  [SEARCH_PIN_SOURCE]: 'id',
}

// Setting just shy of 2 to mitigate rendering artifacts caused by
// https://github.com/mapbox/mapbox-gl-js/issues/11789
export const MAP_MIN_ZOOM = 2.1
export const MAP_MAX_ZOOM = 22

export const CARD_SCROLL_TRACK_TYPES = Object.freeze({
  ALL_CONTENT: 'all-content-list',
  MARKUP_LIST: 'markup-type-content-list',
  FOLDER_LIST: 'folder-content-list',
})

/**
 * State codes
 * NOTE: Canada is an exception since it is technically a state value on the /v2/subscriptions API
 */
export const STATE_CODES = Object.freeze({
  CANADA: 'can',
  ALABAMA: 'al',
  ALASKA: 'ak',
  ARIZONA: 'az',
  ARKANSAS: 'ar',
  CALIFORNIA: 'ca',
  COLORADO: 'co',
  CONNECTICUT: 'ct',
  DELAWARE: 'de',
  FLORIDA: 'fl',
  GEORGIA: 'ga',
  HAWAII: 'hi',
  IDAHO: 'id',
  ILLINOIS: 'il',
  INDIANA: 'in',
  IOWA: 'ia',
  KANSAS: 'ks',
  KENTUCKY: 'ky',
  LOUISIANA: 'la',
  MAINE: 'me',
  MARYLAND: 'md',
  MASSACHUSETTS: 'ma',
  MICHIGAN: 'mi',
  MINNESOTA: 'mn',
  MISSISSIPPI: 'ms',
  MISSOURI: 'mo',
  MONTANA: 'mt',
  NEBRASKA: 'ne',
  NEVADA: 'nv',
  NEW_HAMPSHIRE: 'nh',
  NEW_JERSEY: 'nj',
  NEW_MEXICO: 'nm',
  NEW_YORK: 'ny',
  NORTH_CAROLINA: 'nc',
  NORTH_DAKOTA: 'nd',
  OHIO: 'oh',
  OKLAHOMA: 'ok',
  OREGON: 'or',
  PENNSYLVANIA: 'pa',
  RHODE_ISLAND: 'ri',
  SOUTH_CAROLINA: 'sc',
  SOUTH_DAKOTA: 'sd',
  TENNESSEE: 'tn',
  TEXAS: 'tx',
  UTAH: 'ut',
  VERMONT: 'vt',
  VIRGINIA: 'va',
  WASHINGTON: 'wa',
  WEST_VIRGINIA: 'wv',
  WISCONSIN: 'wi',
  WYOMING: 'wy',
})

/**
 * ISO 3166-1 alpha-2 Country codes
 */
export const COUNTRY_CODES = Object.freeze({
  UNITED_STATES: 'us',
  CANADA: 'ca',
  MEXICO: 'mx',
})

/**
 * ISO 3166-2:US state codes
 */
export const US_STATE_CODES = Object.freeze({
  'US-AL': 'Alabama',
  'US-AK': 'Alaska',
  'US-AZ': 'Arizona',
  'US-AR': 'Arkansas',
  'US-CA': 'California',
  'US-CO': 'Colorado',
  'US-CT': 'Connecticut',
  'US-DE': 'Delaware',
  'US-FL': 'Florida',
  'US-GA': 'Georgia',
  'US-HI': 'Hawaii',
  'US-ID': 'Idaho',
  'US-IL': 'Illinois',
  'US-IN': 'Indiana',
  'US-IA': 'Iowa',
  'US-KS': 'Kansas',
  'US-KY': 'Kentucky',
  'US-LA': 'Louisiana',
  'US-ME': 'Maine',
  'US-MD': 'Maryland',
  'US-MA': 'Massachusetts',
  'US-MI': 'Michigan',
  'US-MN': 'Minnesota',
  'US-MS': 'Mississippi',
  'US-MO': 'Missouri',
  'US-MT': 'Montana',
  'US-NE': 'Nebraska',
  'US-NV': 'Nevada',
  'US-NH': 'New Hampshire',
  'US-NJ': 'New Jersey',
  'US-NM': 'New Mexico',
  'US-NY': 'New York',
  'US-NC': 'North Carolina',
  'US-ND': 'North Dakota',
  'US-OH': 'Ohio',
  'US-OK': 'Oklahoma',
  'US-OR': 'Oregon',
  'US-PA': 'Pennsylvania',
  'US-RI': 'Rhode Island',
  'US-SC': 'South Carolina',
  'US-SD': 'South Dakota',
  'US-TN': 'Tennessee',
  'US-TX': 'Texas',
  'US-UT': 'Utah',
  'US-VT': 'Vermont',
  'US-VA': 'Virginia',
  'US-WA': 'Washington',
  'US-WV': 'West Virginia',
  'US-WI': 'Wisconsin',
  'US-WY': 'Wyoming',
})
